import { useEffect } from 'react';
import { css } from '@emotion/react';
import { ScheduleMatchOpen } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, PopoverMenu, RadioField, useFormField, usePopoverMenu } from '@frontend/design-system';
import { FilterTraySection } from '../filter-tray-section';

export interface PhoneHoursSelectorProps {
  value?: ScheduleMatchOpen;
  onChange: (value: ScheduleMatchOpen) => void;
  label?: string;
  trackingId?: string;
  /** Renders the inline popover variant by default; set to render the filter-tray variant instead. */
  isTrayFilter?: boolean;
}

/**
 * Reusable Phone Hours filter shared across analytics pages.
 *
 * Encapsulates the chip trigger + popover + `ScheduleMatchOpen` radio group
 * (All / Open / Closed hours) so consuming pages don't re-implement it.
 */
export const PhoneHoursSelector = ({
  value,
  onChange,
  label,
  trackingId,
  isTrayFilter = false,
}: PhoneHoursSelectorProps) => {
  const { t } = useTranslation('analytics');
  const { getMenuProps, getTriggerProps } = usePopoverMenu({ placement: 'bottom' });
  const resolvedLabel = label ?? t('Phone Hours');

  const fieldProps = useFormField({ type: 'radio', value: value ?? ScheduleMatchOpen.ALL }, [value]);

  useEffect(() => {
    onChange(fieldProps.value as ScheduleMatchOpen);
  }, [fieldProps.value]);

  const isFilterActive = !!value && value !== ScheduleMatchOpen.ALL;

  if (isTrayFilter) {
    return (
      <FilterTraySection
        label={resolvedLabel}
        onReset={() => onChange(ScheduleMatchOpen.ALL)}
        disableReset={!isFilterActive}
      >
        <RadioField {...fieldProps} css={styles} name='phone-hours' data-trackingid={trackingId}>
          <RadioField.Radio value='ALL'>{t('All Hours')}</RadioField.Radio>
          <RadioField.Radio value='OPEN'>{t('Open Hours')}</RadioField.Radio>
          <RadioField.Radio value='CLOSED'>{t('Closed Hours')}</RadioField.Radio>
        </RadioField>
      </FilterTraySection>
    );
  }

  return (
    <>
      <Chip.DropdownFilter
        {...getTriggerProps()}
        filterIsActive={isFilterActive}
        leftElement={<Icon name='clock-small' color='default' />}
        trackingId={trackingId}
        css={chipStyles}
      >
        {resolvedLabel}
      </Chip.DropdownFilter>
      <PopoverMenu {...getMenuProps()}>
        <RadioField {...fieldProps} css={[styles, extraPadding]} name='phone-hours' data-trackingid={trackingId}>
          <RadioField.Radio value='ALL'>{t('All Hours')}</RadioField.Radio>
          <RadioField.Radio value='OPEN'>{t('Open Hours')}</RadioField.Radio>
          <RadioField.Radio value='CLOSED'>{t('Closed Hours')}</RadioField.Radio>
        </RadioField>
      </PopoverMenu>
    </>
  );
};

const chipStyles = css`
  max-width: none;
`;

const styles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  > div {
    margin-bottom: 0;
  }
`;

const extraPadding = css`
  padding: ${theme.spacing(2)};
`;
